import { create } from 'zustand';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { GamificationProfile } from '@/types';

interface SeenState {
  initialized: boolean;
  level: number;
  badges: string[];
}

function storageKey(userId: string) {
  return `gamification_seen_${userId}`;
}

interface GamificationState {
  profile: GamificationProfile | null;
  loading: boolean;
  error: string | null;
  fetch: (userId: string) => Promise<void>;
  reset: () => void;
}

export const useGamificationStore = create<GamificationState>((set) => ({
  profile: null,
  loading: false,
  error: null,

  reset: () => set({ profile: null, loading: false, error: null }),

  fetch: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const res = await api.get<{ success: boolean; data?: GamificationProfile }>('/gamification/me');
      if (!res.success || !res.data) {
        set({ loading: false });
        return;
      }
      const data = res.data;

      const raw = sessionStorage.getItem(storageKey(userId));
      let prev: SeenState | null = null;
      try {
        prev = raw ? (JSON.parse(raw) as SeenState) : null;
      } catch {
        prev = null;
      }

      if (prev?.initialized) {
        if (data.level > prev.level) {
          toast.success(`Neues Level: ${data.level}`, { description: 'Weiter so.' });
        }
        const prevBadges = new Set(prev.badges);
        for (const b of data.badges) {
          if (!prevBadges.has(b.slug)) {
            toast.success(`Badge: ${b.title}`, { description: b.description ?? undefined });
          }
        }
      }

      sessionStorage.setItem(
        storageKey(userId),
        JSON.stringify({
          initialized: true,
          level: data.level,
          badges: data.badges.map((b) => b.slug),
        } satisfies SeenState)
      );

      set({ profile: data, loading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },
}));
